<template>
  <v-form ref="signUpForm" @submit.prevent="signUp" class="d-flex justify-center flex-column">
    <v-card-text class="d-flex flex-column align-center pb-0" :class="!$vuetify.breakpoint.lgAndUp && 'px-0 mx-0'">
      <v-text-field dense v-model="form.name" :rules="$rules.requiredRules" :label="$t('label.name')" class="text-field-styling w-100" outlined rounded />
      <v-text-field dense v-model="form.email" :rules="$rules.emailRules" :label="$t('label.email')" class="text-field-styling w-100" outlined rounded :error-messages="emailErrors" />
      <MobileNumberField v-model="form.mobileNumber" :rules="$rules.mobileNumberRules" :error-messages="mobileNumberErrors" />
      <DOBPicker :rules="$rules.requiredRules" :label="$t('label.dob')" v-model="form.birthday" class="text-field-styling" />
      <p class="mb-2">{{ $t('label.gender') }}</p>
      <v-radio-group v-model="form.gender" row class="mt-0" :rules="$rules.requiredRules">
        <v-radio :label="$t('label.male')" value="1"></v-radio>
        <v-radio :label="$t('label.female')" value="2"></v-radio>
      </v-radio-group>
      <v-text-field
        v-if="referrer"
        dense
        v-model="form.referralCode"
        :label="$t('label.referralCode')"
        class="text-field-styling w-100"
        :error-messages="referralCodeErrors"
        outlined
        rounded
        :disabled="referrer"
      />

      <v-expand-transition>
        <p class="error--text text-center text-mini" v-show="submissionErrors.smsError">{{ submissionErrors.smsError }}</p>
      </v-expand-transition>
    </v-card-text>

    <div class="d-flex align-start" :class="!$vuetify.breakpoint.smAndUp ? 'px-0 mx-0' : 'px-3'">
      <v-checkbox v-model="isValid" class="mt-0" />
      <div>
        <i18n path="message.signUpAgreement" tag="p" class="secondary--text text-body-2">
          <template #privacyPolicy>
            <router-link class="secondary--text font-weight-bold" :to="{ name: $constants.ROUTE_NAME.PRIVACY_POLICY }">{{ $t('label.privacyPolicy') }}</router-link>
          </template>
          <template #termsOfUse>
            <router-link v-if="$route.query.referralCode" class="secondary--text font-weight-bold" :to="{ name: $constants.ROUTE_NAME.AGENT_TERMS_OF_SERVICE }">
              {{ $t('label.termsOfUse') }}
            </router-link>
            <router-link v-else class="secondary--text font-weight-bold" :to="{ name: $constants.ROUTE_NAME.TERMS_OF_SERVICE }">{{ $t('label.termsOfUse') }}</router-link>
          </template>
        </i18n>
      </div>
    </div>

    <v-card-actions class="d-flex flex-column align-stretch px-4">
      <v-btn color="primary" class="mx-2 mb-5 d-block rounded-pill green-gradient-right text-capitalize override-disabled-styles" :loading="loading" type="submit" :disabled="!isValid">
        {{ $t('label.signUp') }}
      </v-btn>

      <i18n path="message.alreadyHaveAccount" tag="p" class="secondary--text text-center">
        <template #login>
          <button class="text-decoration-underline" @click="$root.$app.openLoginDialog" type="button">{{ $t('label.login') }}</button>
        </template>
      </i18n>
    </v-card-actions>
  </v-form>
</template>

<script>
import { mapActions } from 'vuex'
import MobileNumberField from '@/components/fields/MobileNumberField'
import DOBPicker from '@/components/DOBPicker.vue'
import { OTP_Type } from '@/constants/enums'

export default {
  components: { MobileNumberField, DOBPicker },
  props: { referrer: { type: Boolean, default: false } },
  data() {
    return {
      loading: false,
      form: {
        name: '',
        mobileNumber: '',
        email: '',
        referralCode: this.$route.query.referralCode,
        gender: null,
        birthday: null
      },
      submissionErrors: {
        email: '',
        mobileNumber: '',
        smsError: '',
        referralCode: '',
        gender: '',
        birthday: ''
      },
      isValid: false
    }
  },

  computed: {
    emailErrors() {
      const errors = []
      this.submissionErrors.email && errors.push(this.submissionErrors.email)
      return errors
    },
    mobileNumberErrors() {
      const errors = []
      this.submissionErrors.mobileNumber && errors.push(this.submissionErrors.mobileNumber)
      return errors
    },
    referralCodeErrors() {
      const errors = []
      this.submissionErrors.referralCode && errors.push(this.submissionErrors.referralCode)
      return errors
    },
    dobErrors() {
      const errors = []
      this.submissionErrors.birthday && errors.push(this.submissionErrors.birthday)
      return errors
    }
  },

  methods: {
    ...mapActions('auth', ['login']),
    ...mapActions('common', ['requestOTP', 'verifyEmail', 'verifyReferralCode']),

    clearSubmissionErrors() {
      this.submissionErrors = {
        email: '',
        mobileNumber: '',
        referralCode: '',
        gender: '',
        birthday: ''
      }
    },

    async signUp() {
      this.clearSubmissionErrors()
      if (!this.$refs.signUpForm.validate()) {
        return true
      }
      this.loading = true
      try {
        await this.verifyEmail({
          email: this.form.email
        }).then((res) => {
          if (!res.is_valid) {
            throw { type: 'email', message: 'This email has been taken' }
          }
        })
        if (this.form.referralCode) {
          await this.verifyReferralCode(this.form).then((res) => {
            if (!res.is_valid) {
              throw {
                type: 'referralCode',
                message: 'This is not a valid referral code.'
              }
            }
          })
        }
        await this.requestOTP({
          ...this.form,
          type: OTP_Type.REGISTER
        })
          .then(() => {
            this.$emit('next-step')
          })
          .catch((error) => {
            if (error.error_message) {
              throw {
                type: 'mobileNumber',
                message: error.error_message
              }
            }
            if (error.message) {
              throw {
                type: 'smsError',
                message: error.message
              }
            }
          })
      } catch (error) {
        this.submissionErrors[error.type] = error.message
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.override-disabled-styles {
  &.v-btn--disabled {
    color: white !important;
    opacity: 0.5;
  }
}
</style>
